/**
 * API Configuration Utilities
 *
 * Provides utility functions for API configuration and URL building
 * with environment-specific settings, API versioning, and advanced
 * timeout configurations for different request types.
 */

import { appEnv, getEnv, getEnvSafe } from '../utils/env';

import type { AppEnvironment as Environment } from '../types';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export type RequestType =
  'default' | 'upload' | 'download' | 'auth' | 'realtime';

export interface EnvironmentConfig {
  baseURL: string;
  authBaseURL: string; // Separate base URL for authentication routes
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
  enableLogging: boolean;
  enableDebug: boolean;
}

export interface ApiVersionConfig {
  version: string;
  prefix: string;
  deprecated?: string[];
  supported: string[];
}

// =============================================================================
// ENVIRONMENT CONFIGURATIONS
// =============================================================================

/**
 * Get environment-specific API configurations dynamically
 */
const getEnvironmentConfigs = (): Record<Environment, EnvironmentConfig> => ({
  development: {
    baseURL: getEnvSafe('VITE_API_BASE_URL', 'http://localhost:8000/api'),
    authBaseURL: getEnvSafe('VITE_AUTH_BASE_URL', 'http://localhost:8000'),
    timeout: 45000, // Longer timeout for development/debugging
    retryAttempts: 2,
    retryDelay: 1000,
    enableLogging: true,
    enableDebug: true,
  },
  staging: {
    baseURL: getEnvSafe(
      'VITE_API_BASE_URL',
      'https://staging-api.personaltrainer.app/api'
    ),
    authBaseURL: getEnvSafe(
      'VITE_AUTH_BASE_URL',
      'https://staging-api.personaltrainer.app'
    ),
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1500,
    enableLogging: true,
    enableDebug: false,
  },
  production: {
    baseURL: getEnvSafe(
      'VITE_API_BASE_URL',
      'https://api.personaltrainer.app/api'
    ),
    authBaseURL: getEnvSafe(
      'VITE_AUTH_BASE_URL',
      'https://api.personaltrainer.app'
    ),
    timeout: 25000, // Shorter timeout for production
    retryAttempts: 3,
    retryDelay: 2000,
    enableLogging: false,
    enableDebug: false,
  },
  // A Maestro build behaves as production does; only its hosts differ. They
  // point at a dead port, as the web e2e bundle's do, so a request no shared
  // handler answers fails instead of reaching a real API.
  e2e: {
    baseURL: getEnvSafe('VITE_API_BASE_URL', 'http://localhost:9999/api'),
    authBaseURL: getEnvSafe('VITE_AUTH_BASE_URL', 'http://localhost:9999'),
    timeout: 25000,
    retryAttempts: 3,
    retryDelay: 2000,
    enableLogging: false,
    enableDebug: false,
  },
});

/**
 * Get API Version Configuration dynamically
 */
const getApiVersionConfig = (): ApiVersionConfig => ({
  version: getEnvSafe('VITE_API_VERSION', 'v1'),
  prefix: '/api',
  deprecated: ['v0'], // List of deprecated versions
  supported: ['v1', 'v2'], // List of supported versions
});

/**
 * Request type timeout configurations (in milliseconds)
 */
const REQUEST_TIMEOUT_CONFIG: Record<RequestType, number> = {
  default: 30000, // 30 seconds
  auth: 15000, // 15 seconds - faster auth responses
  upload: 120000, // 2 minutes - file uploads
  download: 180000, // 3 minutes - file downloads
  realtime: 5000, // 5 seconds - realtime/websocket connections
};

/**
 * Get the current environment — `appEnv()` under the name this module's
 * callers already use.
 */
export const getCurrentEnvironment = (): Environment => appEnv();

/**
 * Get environment-specific configuration
 */
export const getEnvironmentConfig = (env?: Environment): EnvironmentConfig => {
  const currentEnv = env || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  return configs[currentEnv];
};

/**
 * Get the base API URL for the current environment with version support
 */
export const getBaseApiUrl = (options?: {
  environment?: Environment;
  version?: string;
  includeVersion?: boolean;
}): string => {
  const env = options?.environment || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  const config = configs[env];
  const versionConfig = getApiVersionConfig();
  const version = options?.version || versionConfig.version;
  const includeVersion = options?.includeVersion ?? true;

  let baseUrl = config.baseURL;

  // Add version to URL if requested and not already present
  if (includeVersion && !baseUrl.includes(`/${version}`)) {
    // Remove /api suffix if present, add version, then add /api back
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4); // Remove '/api'
      baseUrl = `${baseUrl}/api/${version}`;
    } else {
      baseUrl = `${baseUrl}/${version}`;
    }
  }

  return baseUrl;
};

/**
 * Get the base authentication URL for the current environment
 * This is used for Laravel Sanctum auth routes which are typically in the web group
 */
export const getAuthBaseUrl = (options?: {
  environment?: Environment;
}): string => {
  const env = options?.environment || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  const config = configs[env];
  return config.authBaseURL;
};

/**
 * Build full API URL with endpoint, supporting version and environment overrides
 */
export const buildApiUrl = (
  endpoint: string,
  options?: {
    environment?: Environment;
    version?: string;
    includeVersion?: boolean;
  }
): string => {
  const baseUrl = getBaseApiUrl(options);

  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

  // If endpoint already includes api/ or version, don't modify the base URL
  const hasApiPrefix = cleanEndpoint.startsWith('api/');
  const versionConfig = getApiVersionConfig();
  const hasVersionPrefix = versionConfig.supported.some((v) =>
    cleanEndpoint.startsWith(`${v}/`)
  );

  if (hasApiPrefix || hasVersionPrefix) {
    // Use base URL without /api suffix for already prefixed endpoints
    const rootUrl = baseUrl.split('/api')[0];
    return `${rootUrl}/${cleanEndpoint}`;
  }

  return `${baseUrl}/${cleanEndpoint}`;
};

/**
 * Check if we're in debug mode with environment-aware defaults
 */
export const isDebugMode = (env?: Environment): boolean => {
  const currentEnv = env || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  const envConfig = configs[currentEnv];

  // Explicitly check for 'false' string to allow overriding defaults
  if (getEnv('VITE_DEBUG_API') === 'false') {
    return false;
  }

  // Explicitly check for 'true' string to force enable debug
  if (getEnv('VITE_DEBUG_API') === 'true') {
    return true;
  }

  // Fall back to environment-specific default
  return envConfig.enableDebug;
};

/**
 * Check if logging is enabled for the current environment
 */
export const isLoggingEnabled = (env?: Environment): boolean => {
  const currentEnv = env || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  const envConfig = configs[currentEnv];

  // Allow explicit override via environment variable
  if (getEnv('VITE_ENABLE_LOGGING') === 'false') {
    return false;
  }

  if (getEnv('VITE_ENABLE_LOGGING') === 'true') {
    return true;
  }

  // Fall back to environment-specific default
  return envConfig.enableLogging;
};

/**
 * Get timeout for specific request types with environment consideration
 */
export const getTimeoutForRequestType = (
  type: RequestType,
  env?: Environment
): number => {
  const currentEnv = env || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  const envConfig = configs[currentEnv];

  // Get base timeout from request type configuration
  const baseTimeout = REQUEST_TIMEOUT_CONFIG[type];

  // Apply environment-specific multiplier for development
  if (currentEnv === 'development') {
    return Math.max(baseTimeout, envConfig.timeout); // Use longer of the two
  }

  return baseTimeout;
};

/**
 * Get retry configuration for the current environment
 */
export const getRetryConfig = (
  env?: Environment
): { attempts: number; delay: number } => {
  const currentEnv = env || getCurrentEnvironment();
  const configs = getEnvironmentConfigs();
  const envConfig = configs[currentEnv];

  return {
    attempts: envConfig.retryAttempts,
    delay: envConfig.retryDelay,
  };
};

/**
 * Get API version information and validation
 */
export const getApiVersion = (): string => {
  const versionConfig = getApiVersionConfig();
  return versionConfig.version;
};

/**
 * Check if an API version is supported
 */
export const isVersionSupported = (version: string): boolean => {
  const versionConfig = getApiVersionConfig();
  return versionConfig.supported.includes(version);
};

/**
 * Check if an API version is deprecated
 */
export const isVersionDeprecated = (version: string): boolean => {
  const versionConfig = getApiVersionConfig();
  return versionConfig.deprecated?.includes(version) ?? false;
};

/**
 * Get all supported API versions
 */
export const getSupportedVersions = (): string[] => {
  const versionConfig = getApiVersionConfig();
  return [...versionConfig.supported];
};

/**
 * Validate and normalize API version
 */
export const normalizeApiVersion = (version?: string): string => {
  const versionConfig = getApiVersionConfig();
  const targetVersion = version || versionConfig.version;

  if (!isVersionSupported(targetVersion)) {
    console.warn(
      `API version ${targetVersion} is not supported. Falling back to ${versionConfig.version}`
    );
    return versionConfig.version;
  }

  if (isVersionDeprecated(targetVersion)) {
    console.warn(
      `API version ${targetVersion} is deprecated. Consider upgrading to ${versionConfig.version}`
    );
  }

  return targetVersion;
};

/**
 * Build versioned endpoint URL
 */
export const buildVersionedEndpoint = (
  endpoint: string,
  version?: string,
  environment?: Environment
): string => {
  const normalizedVersion = normalizeApiVersion(version);
  return buildApiUrl(endpoint, {
    version: normalizedVersion,
    ...(environment !== undefined && { environment }),
    includeVersion: true,
  });
};
